import React from 'react'
import { Pagination, Select as AntSelect, Table } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import isNil from 'lodash/isNil'
import type { ColumnsType } from 'antd/es/table'
import {
  ModalScrollBody,
  UploadErrorBanner,
  UploadErrorBannerHeader,
  UploadErrorGroup,
  UploadErrorGroupHeader,
  UploadErrorGroupHint,
  UploadErrorRowBadge,
  UploadErrorRowBadgeList,
  UploadPaginationBar,
  UploadPreviewFileMeta,
  UploadPreviewFileName,
  UploadPreviewTable,
} from './ClientBillingProfileManagement.styles'
import type {
  ErrorGroupConfig,
  ErrorRowIndex,
  ParsedRow,
} from './bulkUpload.helpers'
import { describePlanSummary } from './bulkUploadPlan'
import type { PlanSummary, RowPlan } from './bulkUploadPlan'

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200].map((size) => ({
  label: String(size),
  value: size,
}))

/** Every issue in the file, grouped by cause, with the rows that carry each. */
const BulkUploadErrorBanner = ({
  groups,
  totalIssueCount,
  totalErrorRowCount,
}: {
  groups: ErrorGroupConfig[]
  totalIssueCount: number
  totalErrorRowCount: number
}) => (
  <UploadErrorBanner>
    <WarningOutlined style={{ marginTop: 3, flexShrink: 0 }} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <UploadErrorBannerHeader>
        {totalIssueCount} {totalIssueCount === 1 ? 'issue' : 'issues'} across{' '}
        {totalErrorRowCount} {totalErrorRowCount === 1 ? 'row' : 'rows'}. Fix
        before uploading.
      </UploadErrorBannerHeader>
      {groups.map((group) => (
        <UploadErrorGroup key={group.key}>
          <UploadErrorGroupHeader>
            <span>
              {group.label} ({group.rows.length})
            </span>
            {group.hint ? (
              <UploadErrorGroupHint>{group.hint}</UploadErrorGroupHint>
            ) : null}
          </UploadErrorGroupHeader>
          <UploadErrorRowBadgeList>
            {group.rows.map((row) => (
              <UploadErrorRowBadge key={row.rowNum}>
                Row {row.rowNum}
                {isNil(row.value) ? null : <em>: &quot;{row.value}&quot;</em>}
              </UploadErrorRowBadge>
            ))}
          </UploadErrorRowBadgeList>
        </UploadErrorGroup>
      ))}
    </div>
  </UploadErrorBanner>
)

interface BulkUploadPreviewStepProps {
  fileName: string | null
  rowCount: number
  pagedRows: ParsedRow[]
  columns: ColumnsType<ParsedRow>
  errorGroups: ErrorGroupConfig[]
  errorIndex: ErrorRowIndex
  plansByRow: Map<number, RowPlan>
  planSummary: PlanSummary
  isPlanLoading: boolean
  isPlanError: boolean
  pageSize: number
  currentPage: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

/** The file as it will be applied: what each row does, and what blocks it. */
const BulkUploadPreviewStep = ({
  fileName,
  rowCount,
  pagedRows,
  columns,
  errorGroups,
  errorIndex,
  plansByRow,
  planSummary,
  isPlanLoading,
  isPlanError,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
}: BulkUploadPreviewStepProps) => {
  const getRowClassName = (row: ParsedRow) => {
    if (errorIndex.invalidRowNums.has(row.rowNum)) return 'row-invalid'
    if (errorIndex.unmatchedRowNums.has(row.rowNum)) {
      return 'row-client-unmatched'
    }
    if (plansByRow.get(row.rowNum)?.kind === 'needsEmails') return 'row-invalid'
    return ''
  }

  const planText = describePlanSummary(planSummary)

  return (
    <ModalScrollBody>
      <div>
        <UploadPreviewFileName>{fileName}</UploadPreviewFileName>
        <UploadPreviewFileMeta>
          {rowCount} {rowCount === 1 ? 'row' : 'rows'}
          {isPlanLoading ? ' · checking stored profiles…' : null}
          {isPlanError
            ? ' · could not check stored profiles. Close and try again.'
            : null}
          {!isPlanLoading && !isPlanError && planText !== ''
            ? ` · ${planText}`
            : null}
        </UploadPreviewFileMeta>
      </div>

      {errorGroups.length > 0 ? (
        <BulkUploadErrorBanner
          groups={errorGroups}
          totalIssueCount={errorIndex.totalIssueCount}
          totalErrorRowCount={errorIndex.totalErrorRowCount}
        />
      ) : null}

      <UploadPreviewTable>
        <Table
          dataSource={pagedRows}
          columns={columns}
          rowKey="rowNum"
          size="small"
          pagination={false}
          scroll={{ x: 900 }}
          rowClassName={getRowClassName}
        />
      </UploadPreviewTable>

      {rowCount > pageSize ? (
        <UploadPaginationBar>
          <span>
            Rows per page
            <AntSelect
              size="small"
              showSearch={false}
              value={pageSize}
              onChange={onPageSizeChange}
              style={{ width: 70 }}
              options={PAGE_SIZE_OPTIONS}
            />
          </span>
          <div className="upload-pagination">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={rowCount}
              onChange={onPageChange}
              showSizeChanger={false}
              showTotal={(total, range) =>
                `${range[0]}–${range[1]} of ${total}`
              }
              size="small"
            />
          </div>
        </UploadPaginationBar>
      ) : null}
    </ModalScrollBody>
  )
}

export { BulkUploadPreviewStep }
